import { useEffect, useState, useCallback } from 'react'
import { withRouter } from 'react-router'
import { Facet } from '../FacetControl/Types'
import { InfoButton } from 'components/buttons'
import DrawerModal from 'components/modal/drawer-modal'
import { FacetPanel } from '../FacetPanel'
import { track, useTracking } from 'lib/analytics'
import {
  constructFilterQueryString,
  parseFilterQueryParams,
} from '../../../../lib'
import { filterEmptyFilters } from '../../../product-filters/helpers'
import { useCatalogCategoryFacets } from '../../../../api/hooks'
import css from './FacetsBar.styles.scss'

interface SelectedFilters {
  [key: string]: string[] | undefined
}

interface FacetsBarProps {
  categorySlug: string
  facets: Facet<any>[]
  router: any
}

const FacetsBar: React.FC<FacetsBarProps> = ({ categorySlug, router }) => {
  const tracker = useTracking()
  const [showModal, setShowModal] = useState(false)
  const [initialOpenSection, setInitialOpenSection] = useState('')
  const [originalFilters, setOriginalFilters] = useState<SelectedFilters>({})
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(
    () => {
      const initialFilters = router.location.search
        ? parseFilterQueryParams(router.location.search)
        : {}
      return initialFilters as SelectedFilters
    }
  )

  const [selectedSort, setSelectedSort] = useState(
    new URLSearchParams(router.location.search).get('sort') || ''
  )

  useEffect(() => {
    const newFilters = parseFilterQueryParams(
      router.location.search
    ) as SelectedFilters
    setSelectedFilters(newFilters)
  }, [router.location.search])

  const closePanel = useCallback(
    (revertChanges = false) => {
      if (revertChanges) {
        setSelectedFilters(originalFilters) // Revert changes when closing the panel
      }
      setShowModal(false)
    },
    [originalFilters]
  )

  // @ts-ignore
  const { data, isLoading } = useCatalogCategoryFacets(
    categorySlug,
    selectedFilters
  )
  const facetsToDisplay = isLoading ? [] : data.facets

  let sortCriteria = ''
  let sortOrder = ''

  switch (selectedSort) {
    case 'price_asc':
      sortCriteria = 'Price'
      sortOrder = 'ASC'
      break
    case 'price_desc':
      sortCriteria = 'Price'
      sortOrder = 'DESC'
      break
    case 'newest':
      sortCriteria = 'Newest'
      sortOrder = 'DESC'
      break
    case 'highest_rated':
      sortCriteria = 'Rating'
      sortOrder = 'DESC'
      break
    case 'most_reviewed':
      sortCriteria = 'Review Count'
      sortOrder = 'DESC'
      break
    default:
      sortCriteria = 'Popular'
      sortOrder = 'DESC'
  }

  const applyFilters = useCallback(() => {
    const nonEmptyFilters = filterEmptyFilters(selectedFilters)
    const filterQueryString = constructFilterQueryString(nonEmptyFilters)
    const sortQueryString = selectedSort ? `sort=${selectedSort}` : ''
    const queryString = [filterQueryString, sortQueryString]
      .filter(Boolean)
      .join('&')
    router.push(`${window.location.pathname}?${queryString}`)

    tracker.trackEvent({
      event: track.filtersApplied,
      filters: JSON.stringify(nonEmptyFilters),
      storeCategorySlug: window?.location?.pathname?.split('/')?.pop(),
      sortCriteria,
      sortOrder,
    })
    closePanel()
  }, [selectedFilters, selectedSort, closePanel])

  const handleClearFilters = useCallback(() => {
    setSelectedFilters({})
    setSelectedSort('')
    router.push(`${window.location.pathname}`)
    closePanel()
  }, [])

  const handleFilterChange = useCallback((facetKey, selectedOptions) => {
    setSelectedFilters((prevFilters) => {
      const newFilters = { ...prevFilters }
      if (selectedOptions.length === 0) {
        delete newFilters[facetKey]
      } else {
        newFilters[facetKey] = selectedOptions
      }
      return newFilters
    })
  }, [])

  const handleSortChange = useCallback((sortValue) => {
    setSelectedSort(sortValue)
  }, [])

  const openPanel = useCallback(
    (focusedSection) => {
      setShowModal(true)
      setOriginalFilters(selectedFilters)
      setInitialOpenSection(focusedSection)

      tracker.trackEvent({
        event: track.filtersPanelToggled,
        focusedSection,
        isOpen: true,
        storeCategorySlug: window?.location?.pathname?.split('/')?.pop(),
      })
    },
    [selectedFilters]
  )

  const renderTriggerButtons = () => (
    <>
      <InfoButton
        empty={selectedSort == ''}
        pill
        size="sm"
        onClick={() => openPanel('sort')}
      >
        Sort
      </InfoButton>
      {facetsToDisplay.map((facet: Facet<any>) => (
        <InfoButton
          className="hidden-xs"
          empty={
            !selectedFilters[facet.key] ||
            selectedFilters[facet.key]?.length === 0
          }
          pill
          key={facet.key}
          size="sm"
          onClick={() => openPanel(facet.key)}
        >
          {facet.label}
        </InfoButton>
      ))}
      <InfoButton
        className="visible-xs"
        empty={!Object.keys(selectedFilters).length}
        pill
        size="sm"
        onClick={openPanel}
      >
        Filter
      </InfoButton>
      <InfoButton
        className="visible-xs"
        empty={!selectedFilters['inventory_available']}
        pill
        size="sm"
        onClick={() => openPanel('inventory_available')}
      >
        In Stock
      </InfoButton>
    </>
  )

  return (
    <DrawerModal
      origin="left"
      show={showModal}
      onHide={() => closePanel(true)}
      trigger={<div className={css.FacetsBar}>{renderTriggerButtons()}</div>}
      style={undefined}
    >
      <FacetPanel
        closePanel={closePanel}
        facets={facetsToDisplay}
        initialOpenSection={initialOpenSection}
        onFilterChange={handleFilterChange}
        onApplyFilters={applyFilters}
        onClearFilters={handleClearFilters}
        onSortChange={handleSortChange}
        selectedFilters={selectedFilters}
        selectedSort={selectedSort}
      />
    </DrawerModal>
  )
}

export default withRouter(FacetsBar)
